import {
  T,
  cycleExtra,
  extrasCents,
  extrasText,
  formatCents,
  ingredientGroups,
  ingredientPriceCents,
  parseExtras,
  tt,
  type BuilderIngredient,
  type ChosenExtras,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Check, Pizza, X } from 'lucide-react'
import { useState } from 'react'

/**
 * *Prísady navyše* — the customer's version, FRONTEND.md §9.168.
 *
 * **The owner's report:** *"when adding pizza to košík, old system using modal for 'pridať veci
 * navyše', in our new system that is not implemented in customer"*. Correct: the manager has had
 * this since §9.41 and the customer app had nothing.
 *
 * **Asked before the add, not after.** That is the opposite of `AddonPrompt` beside it, and both
 * follow legacy. An add-on is a *second product* — a customer who dismisses that question still has
 * what they came for, so it is added first. A topping changes the line itself, so there is nothing
 * to add until the answer is in. Legacy's `skipPizzaToppingModal` adds the plain pizza, which is
 * why *Preskočiť* here is not a cancel.
 *
 * **Tap once for a portion, again for a double, again to remove it** — legacy's cycle, and the
 * reason it is not a checkbox is that a double portion of ham is an ordinary order.
 *
 * The prices shown are the client's, from `ingredientForSize`. The server charges from the ids by
 * the same rule (§10A.80), so the quote and the bill agree to the cent — which they did not before,
 * because until §10A.80 the server charged nothing at all.
 */

export interface PizzaExtrasPromptProps {
  productName: string
  /** The size the pizza is being bought at, in centimetres. A topping costs more on a bigger one. */
  sizeCm: number
  ingredients: readonly BuilderIngredient[]
  /** *Preskočiť* — the plain pizza, which is what legacy's skip does. */
  onSkip: () => void
  onConfirm: (
    extras: string,
    extrasSurchargeCents: number,
    chosen: ReadonlyArray<{ ingredientId: number; quantity: number }>,
  ) => void
}

export function PizzaExtrasPrompt(props: PizzaExtrasPromptProps) {
  const groups = ingredientGroups(props.ingredients)
  const offered = groups.flatMap((group) => group.ingredients)
  const [chosen, setChosen] = useState<ChosenExtras>(() => parseExtras('', offered))

  const surcharge = extrasCents(chosen, offered, props.sizeCm)
  const summary = extrasText(chosen, offered)

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      data-pizza-extras-prompt
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-label={tt('Zavrieť')}
        onClick={props.onSkip}
      />
      <section className="relative w-full max-w-md max-h-[85vh] flex flex-col rounded-3xl bg-card border border-border shadow-2xl overflow-hidden">
        <header className="px-5 pt-5 pb-3 border-b border-border shrink-0 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              <Pizza className="w-5 h-5 text-primary" />
              <T>Prísady navyše</T>
            </p>
            <h2 className="text-lg font-bold leading-tight truncate">{props.productName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              <T>Kliknite na prísadu pre pridanie navyše (1×) alebo dvojnásobne (2×)</T>
            </p>
          </div>
          <button
            type="button"
            data-extras-close
            aria-label={tt('Zavrieť')}
            onClick={props.onSkip}
            className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {groups.map((group) => (
            <div key={group.category}>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <T>{group.category}</T>
              </p>
              <div className="space-y-1.5">
                {group.ingredients.map((ingredient) => {
                  const qty = chosen.get(ingredient.id)
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      data-extra={ingredient.id}
                      data-extra-qty={qty ?? 0}
                      onClick={() => setChosen((current) => cycleExtra(current, ingredient.id))}
                      className={cn(
                        'w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border transition-all text-left',
                        qty === undefined
                          ? 'border-border hover:border-border/80 hover:bg-muted/30'
                          : 'border-primary/40 bg-primary/5',
                      )}
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        {qty === undefined && (
                          <span className="w-5 h-5 rounded-full border-2 border-border shrink-0" />
                        )}
                        {qty === 1 && (
                          <span className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </span>
                        )}
                        {qty === 2 && (
                          <span className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                            <span className="text-white text-[10px] font-black">2×</span>
                          </span>
                        )}
                        <span className="text-sm font-medium truncate">{ingredient.name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-2 tabular-nums">
                        +{formatCents(ingredientPriceCents(ingredient, props.sizeCm) * (qty ?? 1))}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <footer className="px-5 pb-5 pt-3 border-t border-border shrink-0 space-y-2.5">
          {surcharge > 0 && (
            <div className="flex items-center justify-between text-sm px-1">
              <span className="text-muted-foreground">
                <T>Príplatok za prísady</T>
              </span>
              <span className="font-bold text-primary" data-extras-surcharge>
                +{formatCents(surcharge)}
              </span>
            </div>
          )}
          <button
            type="button"
            data-extras-confirm
            onClick={() =>
              props.onConfirm(
                summary,
                surcharge,
                [...chosen].map(([ingredientId, quantity]) => ({ ingredientId, quantity })),
              )
            }
            className="w-full py-3 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm transition-all"
          >
            <T>Pridať do košíka</T>
            {surcharge > 0 && ` (+${formatCents(surcharge)})`}
          </button>
          {/* Not a cancel. Legacy's skip adds the plain pizza, and a customer who came to buy a
              Margherita and answered *no toppings* has bought a Margherita. */}
          <button
            type="button"
            data-extras-skip
            onClick={props.onSkip}
            className="w-full py-2 rounded-2xl text-muted-foreground text-sm hover:text-foreground transition-colors"
          >
            <T>Preskočiť - bez zmien</T>
          </button>
        </footer>
      </section>
    </div>
  )
}
